import { useEffect, useState } from "react";
import { AlertTriangle, Loader2, Users } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const parse = (text) => [...new Set(text.split(/[\n,;]+/).map((s) => s.trim().toLowerCase()).filter(Boolean))];

/** Who receives the nightly backup link: the founder (env) plus up to N extra addresses. */
export const BackupRecipientsDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [primary, setPrimary] = useState("");
  const [maxExtra, setMaxExtra] = useState(5);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(""); setBusy(false);
    client.get("/admin/backup/recipients").then((r) => {
      setPrimary(r.data.primary);
      setMaxExtra(r.data.max_extra);
      setText(r.data.extra.join("\n"));
    }).catch(() => setError("Couldn't load the current recipients."));
  }, [open, client]);

  const emails = parse(text);
  const bad = emails.filter((e) => !EMAIL_RE.test(e));
  const tooMany = emails.length > maxExtra;
  const valid = !bad.length && !tooMany;

  const save = async () => {
    setBusy(true); setError("");
    try {
      const r = await client.put("/admin/backup/recipients", { extra: emails });
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Couldn't save the recipients.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="backup-recipients-dialog" className="max-w-lg gap-0 border-border bg-sand p-0 sm:rounded-lg">
        <div className="relative overflow-hidden bg-beige px-8 py-7">
          <span className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-cobalt/20 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-sand"><Users className="h-5 w-5" strokeWidth={2} /></span>
            <div>
              <DialogTitle className="font-serif text-3xl font-normal leading-[1.05] text-ink">Who gets the nightly backup</DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed text-olive">
                Each address receives its own copy of the nightly email with the private download link. The link opens the full database, so add only people who should hold donor and student records.
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="space-y-5 px-8 py-6">
          <div>
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Always included</span>
            <p className="mt-2 rounded-md border border-border bg-bone/60 px-4 py-3 text-sm text-ink" data-testid="backup-recipients-primary">{primary || "…"}</p>
          </div>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Additional recipients (up to {maxExtra}, one per line)</span>
            <textarea
              data-testid="backup-recipients-extra"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"greg@example.com\nops@example.com"}
              className="mt-2 w-full resize-none rounded-md border border-border bg-bone px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
            />
          </label>

          {(error || bad.length > 0 || tooMany) && (
            <p role="alert" data-testid="backup-recipients-error" className="flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
              {error || (tooMany ? `At most ${maxExtra} additional addresses.` : `Not an email address: ${bad.join(", ")}`)}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} data-testid="backup-recipients-cancel" className="rounded-full border border-border bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={!valid || busy} data-testid="backup-recipients-save" className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Save recipients
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
